import React, { memo, useCallback } from 'react';
import type { CSSProperties } from 'react';
import type { TextFieldConfig } from '@segmently/schema-models/v2';
import { resolveLabel, resolveAssetReference } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { mapFrameLayoutStyles, mapTextFieldInputStyles, mapEdgeInsetsToCSS } from '@modules/screen-view-v2/utils/styleMapper';
import { useFontOverrides } from '@modules/font-config/context/FontConfigContext';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { getFieldInputAttributes } from './Field.utils';
import { Image } from '../Image/Image';
import { useElementAnimation, mergeAnimationClassName } from '../../../animations';
import styles from './Field.module.css';

export interface FieldProps {
  textField: TextFieldConfig | undefined;
  locale: string;
  value?: string;
  error?: string | null;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  previewMode?: PreviewMode;
}

export const Field = memo(function Field({
  textField,
  locale,
  value = '',
  error = null,
  onChange,
  onBlur,
  onFocus,
  className,
  'data-testid': testId,
  editPath,
  previewMode,
}: FieldProps): React.ReactElement | null {
  const fontOverrides = useFontOverrides();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wantsAutoFocus = (textField as any)?.setFocusWhenScreenAppeared as boolean | undefined;
  // setFocusWhenScreenAppeared is a live-funnel affordance. In editor previews
  // ('edit'/'static') a mounting input must never grab focus: browsers scroll
  // every scrollable ancestor to center the focused element, which yanks the
  // theme/editor page whenever animation replay remounts the subtree.
  const autoFocus = (wantsAutoFocus ?? true)
    && previewMode !== 'edit'
    && previewMode !== 'static';
  // Self-applied screen entrance slot (TextInput `textField`, F14 / AMV-AC-038): plan lookup by the
  // field's own editPath — FlexibleLayout TextField sections have section editPaths, never in the plan.
  // An autofocused field skips its entrance (F14 fix round 1, M6): it takes the focus at mount, so it
  // must never sit invisible at its from-state holding the caret — and focusing it only after the
  // entrance would be an async focus(), which iOS Safari refuses to open the keyboard for.
  const fieldAnim = useElementAnimation(autoFocus ? undefined : editPath);

  // ⚠️ Rules of Hooks: hooks first, the null-textField early-return after —
  // the field can materialize mid-session (theme/editor writes) and a
  // pre-hook return flips the hook count ("Rendered more hooks…" crash).
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  if (!textField) {
    return null;
  }

  const { placeholder, appearance, layout, icon } = textField;

  // V2: fieldSubtype takes priority (Email/Phone come from fieldSubtype in v2)
  const fieldType = textField.fieldSubtype || textField.fieldType;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const keyboardType = (textField as any).keyboardType as string | undefined;
  const inputAttrs = getFieldInputAttributes(fieldType, keyboardType);

  // Resolve placeholder text from LocalizedLabel
  const placeholderText = resolveLabel(placeholder, locale) || '';

  const layoutStyles: CSSProperties = mapFrameLayoutStyles(layout);

  const inputStyle = mapTextFieldInputStyles(textField, fontOverrides);

  // Border properties from TextFieldStyle
  const borderType = appearance?.borderType;
  const borderWidth = appearance?.borderWidth;
  const borderMargins = appearance?.borderMargins;

  // Compute border CSS class based on borderType
  const borderTypeClass = borderType === 'full' ? styles.borderFull
    : borderType === 'bottomTextIcon' ? styles.borderBottomTextIcon
    : ''; // 'bottomText' or undefined → default CSS (border-bottom on .input)

  // Border CSS variables applied to the field container
  const borderVars: CSSProperties = {
    ...(typeof borderWidth === 'number' && { '--field-border-width': `${borderWidth}px` } as CSSProperties),
    ...(appearance?.borderColor && { '--field-border-color': appearance.borderColor } as CSSProperties),
  };

  // Border margins = inner padding between content and the border
  const borderMarginStyle: CSSProperties = borderMargins ? mapEdgeInsetsToCSS(borderMargins) : {};
  // When .fieldWithIcon carries the border (full or bottomTextIcon), padding goes on that wrapper.
  // Otherwise padding goes on the .input element which has the border.
  const borderOnWrapper = (borderType === 'full' || borderType === 'bottomTextIcon');

  const schemaTestId = testId ?? getSchemaTestId(editPath);
  const hasIcon = icon && resolveAssetReference(icon, locale, 'small');

  const inputElement = (
    <>
      <input
        type={inputAttrs.type}
        inputMode={inputAttrs.inputMode}
        autoCapitalize={inputAttrs.autoCapitalize}
        autoCorrect={inputAttrs.autoCorrect}
        autoComplete={inputAttrs.autoComplete}
        autoFocus={autoFocus}
        className={`${styles.input}${error ? ` ${styles.inputError}` : ''}`}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholderText}
        style={{ ...inputStyle, ...(!hasIcon || !borderOnWrapper ? borderMarginStyle : {}) }}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && schemaTestId ? `${schemaTestId}-error` : undefined}
        data-testid={schemaTestId ? `${schemaTestId}.input` : undefined}
      />
      {error && (
        <span
          id={schemaTestId ? `${schemaTestId}-error` : undefined}
          className={styles.error}
          data-testid={schemaTestId ? `${schemaTestId}.error` : undefined}
          role="alert"
        >
          {error}
        </span>
      )}
    </>
  );

  // Combine field class with border type class
  const fieldClassName = [styles.field, borderTypeClass, className].filter(Boolean).join(' ');

  return (
    <div
      className={mergeAnimationClassName(fieldClassName, fieldAnim)}
      style={{ ...layoutStyles, ...borderVars, ...fieldAnim.style }}
      data-anim={fieldAnim['data-anim']}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      {hasIcon ? (
        <div className={styles.fieldWithIcon} style={borderOnWrapper ? borderMarginStyle : undefined}>
          <div className={styles.iconContainer}>
            <Image
              image={icon}
              locale={locale}
              editPath={editPath ? `${editPath}.icon` : undefined}
            />
          </div>
          <div className={styles.inputContainer}>
            {inputElement}
          </div>
        </div>
      ) : (
        inputElement
      )}
    </div>
  );
});

Field.displayName = 'Field';
